import { useState } from 'react'

import type { Slip, SlipStatus, SlipType } from '@/types/storage'
import { SLIP_STATUS_LABELS, SLIP_TYPE_LABELS } from '@/types/storage'
import { useCreateSlip, useUpdateSlip } from './hooks'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface FormState {
  slip_number: string
  type: SlipType
  max_length_ft: string
  status: SlipStatus
}

const EMPTY: FormState = {
  slip_number: '',
  type: 'wet',
  max_length_ft: '',
  status: 'available',
}

interface SlipFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  slip?: Slip | null
}

export function SlipFormDialog({ open, onOpenChange, mode, slip }: SlipFormDialogProps) {
  const createMutation = useCreateSlip()
  const updateMutation = useUpdateSlip()

  const [form, setForm] = useState<FormState>(EMPTY)
  // Snapshot of the seeded values — anything else means unsaved edits.
  const [seed, setSeed] = useState<FormState | null>(null)

  // Reset whenever the dialog opens or the target slip changes ("adjust state
  // during render", same pattern as the deal edit dialog).
  const formKey = `${open}:${mode}:${slip?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open && mode === 'edit' && slip) {
      const seeded = {
        slip_number: slip.slip_number,
        type: slip.type,
        max_length_ft: String(slip.max_length_ft),
        status: slip.status,
      }
      setForm(seeded)
      setSeed(seeded)
    } else if (open) {
      setForm(EMPTY)
      setSeed(EMPTY)
    }
  }

  const isDirty = seed !== null && JSON.stringify(form) !== JSON.stringify(seed)
  const { guardOpenChange, discardGuard } = useDirtyClose(open, onOpenChange, isDirty)

  const maxLength = Number(form.max_length_ft)
  const isValid =
    form.slip_number.trim().length > 0 && Number.isFinite(maxLength) && maxLength > 0

  const handleSubmit = async () => {
    if (!isValid) return
    const body = {
      slip_number: form.slip_number.trim(),
      type: form.type,
      max_length_ft: maxLength,
      status: form.status,
    }
    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(body)
      } else if (slip) {
        await updateMutation.mutateAsync({ id: slip.id, body })
      }
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast; keep dialog open
    }
  }

  const pending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={guardOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New Slip' : 'Edit Slip'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Add a slip or storage spot to the yard.'
              : 'Update this slip’s number, type, capacity, or status.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Slip number</label>
              <Input
                placeholder="A-12"
                value={form.slip_number}
                onChange={(e) => setForm((f) => ({ ...f, slip_number: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Max length (ft)</label>
              <Input
                type="number"
                min={1}
                placeholder="40"
                value={form.max_length_ft}
                onChange={(e) => setForm((f) => ({ ...f, max_length_ft: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v as SlipType }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SLIP_TYPE_LABELS) as SlipType[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {SLIP_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v as SlipStatus }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SLIP_STATUS_LABELS) as SlipStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {SLIP_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={pending || !isValid}>
            {mode === 'create' ? 'Create Slip' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
      {discardGuard}
    </Dialog>
  )
}
